import { useEffect } from "react";
import { Link } from "react-router-dom";

export const ARTICLES = [
  {
    slug: "stripe-webhooks-not-firing-after-deployment",
    title: "Stripe webhooks not firing after deployment — causes and fixes",
    excerpt:
      "Your webhooks were working fine, then you deployed to production and they silently stopped. Here are the three most common causes and exactly how to fix each one.",
    date: "July 19, 2026",
    readTime: "5 min read",
    category: "Stripe",
  },
  {
    slug: "stripe-checkout-not-loading-react-nextjs",
    title: "Stripe Checkout not loading in React or Next.js — how to debug",
    excerpt:
      "Stripe Checkout renders in test mode but breaks in production. Or it loads sometimes but not others. Here is how to diagnose and fix the most common causes.",
    date: "July 19, 2026",
    readTime: "6 min read",
    category: "Stripe",
  },
  {
    slug: "shopify-checkout-broken-after-update",
    title: "Shopify checkout broken after theme update — step by step fix",
    excerpt:
      "A theme update broke your checkout and orders have stopped coming in. Here is how to identify what changed and restore your checkout without losing your customisations.",
    date: "July 19, 2026",
    readTime: "4 min read",
    category: "Shopify",
  },
  {
    slug: "ssl-certificate-expired-emergency-fix",
    title: "SSL certificate expired — how to fix it fast",
    excerpt:
      "Your site is showing a security warning and visitors cannot proceed. Every minute costs you traffic and revenue. Here is the fastest way to renew your SSL certificate.",
    date: "July 19, 2026",
    readTime: "3 min read",
    category: "Infrastructure",
  },
];

const CATEGORY_COLORS = {
  Stripe: "bg-blue-100 text-blue-800 border-blue-200",
  Shopify: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Infrastructure: "bg-blue-100 text-blue-800 border-blue-200",
};

function BlogPage() {
  useEffect(() => {
    document.title = "Payment Recovery Guides — SkillSyncs Intelligence";
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-slate-900 text-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Payment Recovery Guides
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto">
            Technical guides for founders whose payment systems have broken.
            Written by the experts who fix them.
          </p>
        </div>
      </section>

      {/* Article grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {ARTICLES.map((article) => (
            <article
              key={article.slug}
              className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6 sm:p-8 flex flex-col"
            >
              <span
                className={`inline-block self-start text-xs font-semibold px-2.5 py-1 rounded-full border mb-4 ${
                  CATEGORY_COLORS[article.category] ||
                  "bg-slate-100 text-slate-800 border-slate-200"
                }`}
              >
                {article.category}
              </span>

              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 leading-snug">
                {article.title}
              </h2>

              <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-6 flex-1">
                {article.excerpt}
              </p>

              <div className="flex items-center justify-between gap-4 pt-4 border-t border-slate-100">
                <div className="text-xs sm:text-sm text-slate-500">
                  <span>{article.date}</span>
                  <span className="mx-2">·</span>
                  <span>{article.readTime}</span>
                </div>
                <Link
                  to={`/blog/${article.slug}`}
                  className="text-sm font-semibold text-blue-600 hover:text-blue-800 whitespace-nowrap"
                >
                  Read guide →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20">
        <div className="bg-slate-900 rounded-2xl px-6 sm:px-10 py-10 sm:py-14 text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Payment system broken right now?
          </h2>
          <p className="text-slate-300 text-base sm:text-lg mb-8 max-w-xl mx-auto">
            Skip the reading — get an expert on screen in under 2 hours.
          </p>
          <Link
            to="/submit-problem"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-slate-900 font-bold text-base sm:text-lg rounded-xl hover:bg-slate-100 transition-all shadow-xl"
          >
            Get Help Now — from €149
          </Link>
        </div>
      </section>
    </div>
  );
}

export default BlogPage;
